// @flow

import { call, takeEvery, put } from 'redux-saga/effects';
import { type Saga } from 'redux-saga';
import { makeGetRequest, makePostRequest } from 'services/request/request';
import { CHANGE_PASSWORD_REQUEST, FORGOT_PASSWORD_REQUEST, LOGIN_REQUEST, LOGOUT_REQUEST, USER_PROFILE_REQUEST } from './constant';
import {
  changePasswordFailure,
  changePasswordSuccess,
  forgotPasswordFailure,
  forgotPasswordSuccess,
  loginFailure,
  loginSuccess,
  logoutFailure,
  logoutSuccess,
  userProfileFailure,
  userProfileSuccess,
} from './actions';

export function* loginRequest(user: any): Saga<*> {
  try {
    const { payload } = user;
    // Include recaptcha token in the request
    const loginData = {
      username: payload.username,
      password: payload.password, // Send plaintext password (protected by HTTPS)
      recaptchaToken: payload.recaptchaToken,
    };
    const response = yield call(makePostRequest, `/login`, loginData);
    if (response && response.statusCode === 200) {
      // Note: Secure cookies are set by the server with HttpOnly, Secure, and SameSite attributes
      // Client-side cookie setting is removed to prevent security issues and conflicts
      yield put(loginSuccess(response.body));
    }
  } catch (error) {
    yield put(loginFailure({ statusCode: error.statusCode, message: error.message }));
  }
}

export function* userProfileRequest(): Saga<*> {
  try {
    const response = yield call(makeGetRequest, `/user-profile`);
    if (response && response.statusCode === 200) {
      const { body } = response;
      yield put(userProfileSuccess({ ...body }));
    }
  } catch (error) {
    yield put(userProfileFailure({ statusCode: error.statusCode, message: error.message }));
  }
}

export function* changePasswordRequest(changePasswordData: any): Saga<*> {
  try {
    const { payload } = changePasswordData;
    const response = yield call(makePostRequest, `/change-password`, payload);
    if (response && response.statusCode === 202) {
      yield put(changePasswordSuccess());
      yield put({ type: LOGOUT_REQUEST });
    }
  } catch (error) {
    yield put(changePasswordFailure({ statusCode: error.statusCode, message: error.message }));
  }
}

export function* forgotPasswordRequest(forgotPasswordData: any): Saga<*> {
  try {
    const {
      payload: { email, recaptcha },
    } = forgotPasswordData;
    const response = yield call(makePostRequest, `/forgot-password`, { email, recaptchaToken: recaptcha });
    if (response && response.statusCode === 200) {
      yield put(forgotPasswordSuccess());
    }
  } catch (error) {
    console.error('Error in forgotPasswordRequest:', error);
    yield put(forgotPasswordFailure({ statusCode: error.statusCode, message: error.message }));
  }
}

export function* logoutRequest(): Saga<*> {
  try {
    // Get the auth token from cookies
    // Call the logout API to invalidate the session on the server
    // makePostRequest automatically adds Authorization header for non-login endpoints
    const response = yield call(makePostRequest, `/logout`, {});

    if (response && response.statusCode === 200) {
      console.log('Server session invalidated successfully');
    }
    yield put(logoutSuccess());

    // Redirect to login page
    window.location.href = '/login';
  } catch (error) {
    console.error('Error during logout:', error);

    // Even if API call fails, clear cookies locally for security
    yield put(logoutFailure({ statusCode: error.statusCode, message: error.message }));

    // Still redirect to login even on error
    window.location.href = '/login';
  }
}

export default function* userSaga(): Saga<*> {
  yield takeEvery(LOGIN_REQUEST, loginRequest);
  yield takeEvery(LOGOUT_REQUEST, logoutRequest);
  yield takeEvery(USER_PROFILE_REQUEST, userProfileRequest);
  yield takeEvery(CHANGE_PASSWORD_REQUEST, changePasswordRequest);
  yield takeEvery(FORGOT_PASSWORD_REQUEST, forgotPasswordRequest);
}
